"use client"
import { useState } from "react"
import { FaFileUpload, FaFileAlt, FaEye, FaTrash, FaTrashAlt, FaTimes } from "react-icons/fa"
import { getDatabase, ref, push, set, remove, get, update } from "firebase/database"
import { getAuth } from "firebase/auth"

const UploadsSection = ({
  selectedUser,
  mtcFiles = [],
  statementFiles = [],
  formatsFiles = [],
   invoicesFiles = [], 
  uploadProgress = {},
  handleFileUpload, // Now expects: handleFileUpload(e, categoryKey, customFileName?)
  onDeleteFile,     // async (categoryKey, fileId) => ...
  onDeleteAll,      // optional: async (categoryKey) => ...
}) => {
  const [activeSection, setActiveSection] = useState("mtc")
  const [selectMode, setSelectMode] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState(new Set())

  const sections = [
    {
      key: "mtc",
      label: "MTC Documents",
      title: "Material Test Certificate (MTC)",
      description: "Upload MTC files for this user. These will be available to all users.",
      files: mtcFiles,
      inputId: "mtc-upload",
      defaultName: "wk-26_yr-26_site_state",
    },
    {
      key: "statement",
      label: "User Statements",
      title: "User Statements",
      description: "Upload statement files specific to this user.",
      files: statementFiles,
      inputId: "statement-upload",
      defaultName: "Q4th_yr-26_site_state",
    },
    {
      key: "formats",
      label: "Document Formats",
      title: "Document Formats",
      description: "Upload document format templates for this user.",
      files: formatsFiles,
      inputId: "formats-upload",
      defaultName: null, // no rename for formats
    },
    {
  key: "invoices",
  label: "Invoices",
  title: "Invoices",
  description: "Upload invoice files for this user.",
  files: invoicesFiles,   // ✅ THIS MUST MATCH PROP NAME
  inputId: "invoices-upload",
  defaultName: "inv_yr-26_site_state",
}
  ]

  const activeConfig = sections.find((s) => s.key === activeSection)

  const auth = getAuth()
  const db = getDatabase()
  // 🔹 Mark document requests as completed after upload
const markRequestsCompleted = async (categoryKey) => {
  try {
    if (!selectedUser?.uid) return

    const reqRef = ref(db, `document_requests/${selectedUser.uid}/${categoryKey}`)
    const snapshot = await get(reqRef)

    if (!snapshot.exists()) return

    const updates = {}

    snapshot.forEach((child) => {
      const data = child.val()

      if (data?.status === "pending") {
        updates[`${child.key}/status`] = "completed"
      }
    })

    if (Object.keys(updates).length > 0) {
      await update(reqRef, updates)
    }
  } catch (e) {
    console.error("Error updating request status:", e)
  }
}

  // ── Notification logic (unchanged) ──
  const createUploadNotification = async (type) => { /* ... same as before ... */ }

  // ── Custom rename prompt before upload ──
  const promptForNewFileName = (originalName, defaultName) => {
  if (!defaultName) return originalName

  const extension = originalName.split('.').pop() || 'pdf'

  // Generate placeholder based on section
  let placeholder = ""
  const now = new Date()

  if (defaultName?.includes("wk")) {
    // MTC placeholder like: Week-25| Site Address
    const oneJan = new Date(now.getFullYear(),0,1)
    const numberOfDays = Math.floor((now - oneJan) / (24 * 60 * 60 * 1000))
    const week = Math.ceil(( now.getDay() + 1 + numberOfDays) / 7)
    placeholder = `Week-${week}| Site Address`
  } else {
    // Statement placeholder like: Mar26-Q4| Site Address
    const month = now.toLocaleString('en-IN', { month: 'short' })
    const day = String(now.getDate()).padStart(2, "0")
    const year = String(now.getFullYear()).slice(-2)
    const quarter = `Q${Math.floor((now.getMonth() + 3) / 3)}`
    placeholder = `${month}${day}-${quarter}| Site Address`
  }

  // Single input prompt
  const input = window.prompt(
    "Enter file name",
    placeholder
  )

  if (input === null) return null

  const cleanInput = input.trim()
  if (!cleanInput) return null

  return `${cleanInput}.${extension}`
}

// ... rest of your imports and component ...

const handleFileChangeWithNotification = async (e) => {
  if (!e.target.files || !e.target.files[0]) return;

  const file = e.target.files[0];
  const originalName = file.name;

  let finalFileName = originalName;

  // Only prompt for rename in MTC & Statement sections
  if (activeConfig.defaultName) {
    const renamed = promptForNewFileName(originalName, activeConfig.defaultName);
    if (renamed === null) {
      e.target.value = ''; // clear input
      return; // user cancelled
    }
    finalFileName = renamed;
  }

  try {
    // Pass BOTH the event AND the desired final file name
  await handleFileUpload(e, activeSection, finalFileName);

// 🔹 mark requests completed
await markRequestsCompleted(activeSection);

await createUploadNotification(activeSection);
    // Reset file input so user can upload the same file again if needed
    e.target.value = '';
  } catch (err) {
    console.error("Upload failed:", err);
    alert("Upload failed. Please try again.");
  }
};

// ... rest of the component remains exactly the same ...

  // ── Selection & Delete handlers (unchanged) ──
  const enterSelectMode = () => {
    setSelectMode(true)
    setSelectedFiles(new Set())
  }

  const exitSelectMode = () => {
    setSelectMode(false)
    setSelectedFiles(new Set())
  }

  const toggleFileSelection = (fileId) => {
    setSelectedFiles((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(fileId)) newSet.delete(fileId)
      else newSet.add(fileId)
      return newSet
    })
  }

  const handleDeleteSelected = async () => {
    if (selectedFiles.size === 0) return
    if (!window.confirm(`Delete ${selectedFiles.size} selected file(s)?`)) return

    for (const fileId of selectedFiles) {
      if (onDeleteFile) await onDeleteFile(activeSection, fileId)
    }
    exitSelectMode()
  }

  const handleDeleteAll = async () => {
    if (!activeConfig?.files?.length) return
    if (!window.confirm(`Delete ALL ${activeConfig.files.length} files? This cannot be undone.`)) return

    if (onDeleteAll) await onDeleteAll(activeSection)
    exitSelectMode()
  }

  const formatDate = (timestamp) => {
    if (!timestamp) return "Unknown date"
    const date = new Date(timestamp)
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const hasFiles = activeConfig?.files?.length > 0

  return (
    <div className="uploads-wrapper">
      {/* LEFT SIDEBAR – unchanged */}
      <div className="uploads-sidebar">
        <h3 className="sidebar-title">Document Types</h3>
        {sections.map((section) => (
          <div
            key={section.key}
            className={`sidebar-item ${activeSection === section.key ? "active" : ""}`}
            onClick={() => {
              setActiveSection(section.key)
              exitSelectMode()
            }}
          >
            {section.label}
          </div>
        ))}
      </div>

      {/* RIGHT CONTENT */}
      <div className="uploads-content">
        {/* Header + Controls */}
        <div className="section-header" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <FaFileUpload className="section-icon" />
            <div>
              <h2>{activeConfig?.title}</h2>
              <p>{activeConfig?.description}</p>
            </div>
          </div>

          {hasFiles && (
            <div className="delete-controls">
              {!selectMode ? (
                <>
                  <button className="select-mode-btn" onClick={enterSelectMode}>
                    <FaTrash /> Select to Delete
                  </button>
                  <button className="delete-all-btn" onClick={handleDeleteAll}>
                    <FaTrashAlt /> Delete All
                  </button>
                </>
              ) : (
                <>
                  <button className="cancel-btn" onClick={exitSelectMode}>
                    <FaTimes /> Cancel
                  </button>
                  {selectedFiles.size > 0 && (
                    <button className="delete-selected-btn" onClick={handleDeleteSelected}>
                      <FaTrash /> Delete {selectedFiles.size}
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Upload Box */}
        <div className="upload-container">
          <input
            type="file"
            id={activeConfig?.inputId}
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={handleFileChangeWithNotification}
            className="file-input"
          />
          <label htmlFor={activeConfig?.inputId} className="upload-button">
            <FaFileUpload /> Select File
          </label>

          {uploadProgress[activeConfig?.key] != null && (
            <div className="progress-container">
              <div
                className="progress-bar"
                style={{ width: `${uploadProgress[activeConfig?.key]}%` }}
              />
              <span className="progress-text">{uploadProgress[activeConfig?.key]}%</span>
            </div>
          )}
        </div>

        {/* Files List – unchanged */}
        <div className="files-list">
          <h4>
            <FaFileAlt /> Uploaded Files ({activeConfig?.files?.length || 0})
          </h4>

          {hasFiles ? (
            <ul>
              {activeConfig.files.map((file, index) => {
                const fileId = file.id || file.name || String(index)
                const isSelected = selectedFiles.has(fileId)
                const uploadDate = file.uploadDate || file.createdAt || file.timestamp

                return (
                  <li
                    key={fileId}
                    className={`file-item ${selectMode ? "selectable" : ""} ${isSelected ? "selected" : ""}`}
                  >
                    {selectMode && (
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleFileSelection(fileId)}
                        className="file-checkbox"
                      />
                    )}
                    <FaFileAlt className="file-icon" />
                    <div className="file-info">
                      <span className="file-name" title={file.name}>
                        {file.name}
                      </span>
                      <span className="file-date">{formatDate(uploadDate)}</span>
                    </div>
                    <div className="file-actions">
                      <button
                        onClick={() => window.open(file.url, "_blank")}
                        className="action-btn view-btn"
                        title="View File"
                      >
                        <FaEye />
                      </button>
                    </div>
                  </li>
                )
              })}
            </ul>
          ) : (
            <p className="no-files">No files uploaded yet</p>
          )}
        </div>
      </div>
    </div>
  )
}

export default UploadsSection